import { ApiError } from '../core/api/http'
import { isIsoDate, nowStamp } from '../core/format/dates'
import { toCents } from '../core/format/currency'
import { SHIPPING_VAT_RATE, cancelProblem, refundProblem, refundableCents, salesCsv, salesReport, vatRateOf, type OrderProblem, type RevenueOrder } from '../core/rules/revenue'
import { kuurStopRevenue, saleRevenue } from '../core/rules/kassa'
import { saleReturnRevenue } from '../core/rules/saleReturn'
import { isKuurProductId } from '../core/rules/kuur'
import { requestBundleOf } from '../core/rules/orderBundles'
import { HOME_VAT_COUNTRY } from '../core/rules/vatCountry'
import { restoreVoucher } from './voucherBackend'
import { VOUCHER_REFUND_PREFIX } from '../core/rules/orderRefunds'
import { bookSaleStock } from './stockBackend'
import { stockPeriod } from '../core/rules/stockMovements'
import { db, type OrderRow } from './db'
import { isGiftCard, productOf } from './pricing'
import { demoId, reply } from './reply'
import type { AdminBackend } from '../core/types/backend'
import type { AdminOrder, AdminOrderQuery, AdminOrderSummary, OrderEvent, OrderEventKind, OrderFulfilment, RefundLine } from '../core/types/adminRequests'
import type { OrderLineRequest } from '../core/types/requests'

/**
 * DEMO — Bestellingen en Omzet aan de kant van de server (LARAVEL.md §9.6). Rekent met dezelfde
 * regels als de echte server (core/rules/revenue.ts) en weigert met dezelfde codes.
 */

const PAGE_SIZE = 25

/** Welke tijdlijnregel een verzendstap oplevert. Expliciet, zodat alleen geldige soorten in de tijdlijn komen. */
const FULFILMENT_EVENT: Record<OrderFulfilment, OrderEventKind | null> = {
  nieuw: null,
  ingepakt: 'ingepakt',
  verzonden: 'verzonden',
  bezorgd: 'bezorgd',
}

/** Eén tijdlijnregel; het type dwingt een geldige `OrderEventKind` af. */
export const orderEvent = (event: OrderEventKind, at: string = nowStamp(), note?: string): OrderEvent =>
  note ? { at, event, note } : { at, event }

/** Tijdlijnregels voor een nieuwe verzendstap (leeg voor 'nieuw'). */
export const fulfilmentEvents = (status: OrderFulfilment, at: string = nowStamp()): OrderEvent[] => {
  const kind = FULFILMENT_EVENT[status]
  return kind ? [orderEvent(kind, at)] : []
}

const totalCentsOf = (o: OrderRow) =>
  o.total !== undefined ? toCents(o.total) : o.lines.reduce((s, l) => s + toCents(l.price) * l.qty, 0) + toCents(o.shipping)
const refundedOf = (o: OrderRow) => (o.refunds ?? []).reduce((s, r) => s + r.amountCents, 0)
/** De demo betaalt direct: zonder eigen moment telt het plaatsen als betalen. */
const paidAtOf = (o: OrderRow) => (o.payment.status === 'betaald' ? (o.paidAt ?? o.receivedAt) : null)

function toSummary(o: OrderRow): AdminOrderSummary {
  return {
    id: o.id,
    number: o.number ? String(o.number) : null,
    placedAt: o.receivedAt,
    customerName: o.name,
    email: o.request.email,
    totalCents: totalCentsOf(o),
    paymentStatus: o.payment.status,
    fulfilment: o.fulfilment,
    channel: o.viaSubscription ? 'abonnement' : 'webshop',
    refundedCents: refundedOf(o),
    cancelledAt: o.cancelledAt ?? null,
  }
}

const linesOf = (o: OrderRow) =>
  o.lines.map((l) => {
    const p = productOf(l.productId)
    const unitPriceCents = toCents(l.price)
    // Zoals de server: de bundel komt uit de bestelregel van de klant.
    const requested = (o.request?.lines as OrderLineRequest[] | undefined)?.find((r) => r.productId === l.productId)
    const bundleRoutineId = requested ? requestBundleOf(requested) : null
    return {
      ...(bundleRoutineId ? { bundleRoutineId } : {}),
      productId: l.productId,
      name: p?.name ?? l.productId,
      qty: l.qty,
      unitPriceCents,
      vatRate: l.vatRate ?? vatRateOf(p ?? {}),
      vatCountry: l.vatCountry ?? HOME_VAT_COUNTRY,
      lineTotalCents: unitPriceCents * l.qty,
      ...(l.via ? { via: l.via } : {}),
    }
  })

function toDetail(o: OrderRow): AdminOrder {
  const paidAt = paidAtOf(o)
  const timeline: OrderEvent[] = [
    orderEvent('geplaatst', o.receivedAt),
    ...(paidAt ? [orderEvent('betaald', paidAt)] : []),
    ...(o.events ?? []),
  ].sort((a, b) => a.at.localeCompare(b.at))
  return {
    ...toSummary(o),
    address: o.request.address,
    lines: linesOf(o),
    shippingCents: toCents(o.shipping),
    shippingVatRate: SHIPPING_VAT_RATE,
    paymentMethod: o.payment.method,
    gift: o.gift,
    code: o.request.code,
    skinNote: o.request.skinNote,
    ...(o.tracking ? { tracking: o.tracking } : {}),
    timeline,
    refunds: (o.refunds ?? []).map(({ requestId: _requestId, ...r }) => r),
    ...(o.customerId ? { customerId: o.customerId } : {}),
    voucherCents: o.voucherCents ?? 0,
  }
}

const toRevenue = (o: OrderRow): RevenueOrder => ({
  paidAt: paidAtOf(o),
  paymentMethod: o.payment.method,
  channel: 'webshop',
  lines: linesOf(o).map((l) => ({ ...l, giftCard: isGiftCard(l.productId), kuur: isKuurProductId(l.productId) })),
  shippingCents: toCents(o.shipping),
  shippingVatRate: SHIPPING_VAT_RATE,
  refunds: o.refunds ?? [],
  voucherCents: o.voucherCents ?? 0,
})

/** Webshop én kassa: Omzet en de export tellen beide (kassa via core/rules/kassa.ts `saleRevenue`). */
export const allRevenue = (): RevenueOrder[] => [
  ...db.orders().map(toRevenue),
  ...db.sales().flatMap((s) => {
    const r = saleRevenue(s)
    return r ? [r] : []
  }),
  // Gestopte kuren: alleen de terugbetaling, op de dag van stoppen.
  ...db.kuurStops().flatMap((k) => {
    const r = kuurStopRevenue(k)
    return r ? [r] : []
  }),
  // Retouren op bonnen: alleen de terugbetaling, op de dag van de retour.
  ...db.saleReturns().flatMap((x) => {
    // De retour gaat af bij wie de bon uitvoerde (Omzet › Per uitvoerder).
    const r = saleReturnRevenue(x, db.sales().find((s) => s.id === x.saleId)?.staffId)
    return r ? [r] : []
  }),
]

const stateOf = (o: OrderRow) => {
  const s = toSummary(o)
  return {
    paymentStatus: s.paymentStatus,
    fulfilment: s.fulfilment,
    totalCents: s.totalCents,
    refundedCents: s.refundedCents,
    cancelledAt: s.cancelledAt,
    voucherCents: o.voucherCents ?? 0,
  }
}

const fail = (p: OrderProblem): never => {
  throw new ApiError(p.message, p.status, p.field ? { [p.field]: p.message } : undefined, p.code)
}

function matches(o: OrderRow, q: AdminOrderQuery): boolean {
  const s = toSummary(o)
  const term = q.q?.trim().toLowerCase()
  if (term && ![s.customerName, s.email, s.number ?? '', s.id].some((v) => v.toLowerCase().includes(term))) return false
  if (q.channel && s.channel !== q.channel) return false
  const date = s.placedAt.slice(0, 10)
  if (q.from && date < q.from) return false
  if (q.to && date > q.to) return false
  switch (q.status) {
    case undefined:
      return true
    case 'terugbetaald':
      return s.refundedCents > 0
    case 'geannuleerd':
      return s.cancelledAt !== null
    case 'open':
    case 'betaald':
    case 'mislukt':
    case 'verlopen':
      return s.paymentStatus === q.status
    default:
      // Een stand van verzenden: alleen betaalde, niet geannuleerde bestellingen.
      return s.paymentStatus === 'betaald' && s.cancelledAt === null && s.fulfilment === q.status
  }
}

/**
 * Webshopvoorraad bijwerken voor een bestelling: − bij plaatsen (de server reserveert), + bij
 * annuleren. Elke regel komt in het voorraadlog (`verkoop-webshop`, met het bestelnummer). Alleen
 * de webshopplek: salonvoorraad blijft staan.
 */
export function adjustStock(lines: Pick<OrderLineRequest, 'productId' | 'qty'>[], sign: 1 | -1, ref: string) {
  bookSaleStock(lines, 'webshop', sign, ref, sign === 1 ? 'Bestelling geannuleerd' : undefined)
}

const findOrder = (id: string) => db.orders().find((o) => o.id === id) ?? null
const notFound = () => new ApiError('Deze bestelling bestaat niet meer.', 404)

/** Per product wat er nog niet eerder als regel terugbetaald is (voor "alles terug op voorraad"). */
function notYetRefunded(o: OrderRow): RefundLine[] {
  const back: Record<string, number> = {}
  ;(o.refunds ?? []).forEach((r) => r.lines?.forEach((l) => (back[l.productId] = (back[l.productId] ?? 0) + l.qty)))
  return o.lines
    .filter((l) => !isGiftCard(l.productId) && !isKuurProductId(l.productId))
    .map((l) => ({ productId: l.productId, qty: l.qty - (back[l.productId] ?? 0) }))
    .filter((l) => l.qty > 0)
}

const validRefundLines = (o: OrderRow, lines: RefundLine[] | undefined) =>
  (lines ?? []).every((l) => Number.isInteger(l.qty) && l.qty > 0 && o.lines.some((x) => x.productId === l.productId && l.qty <= x.qty))

export const demoSalesBackend: Pick<
  AdminBackend,
  'fetchOrders' | 'fetchOrder' | 'refundOrder' | 'cancelOrder' | 'fetchSalesReport' | 'exportSales'
> = {
  fetchOrders(query) {
    const all = db
      .orders()
      .filter((o) => matches(o, query))
      .sort((a, b) => b.receivedAt.localeCompare(a.receivedAt))
    const pageCount = Math.max(1, Math.ceil(all.length / PAGE_SIZE))
    const page = Math.min(Math.max(1, Math.floor(query.page ?? 1)), pageCount)
    return reply({ items: all.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE).map(toSummary), page, pageCount, total: all.length }, 300)
  },

  async fetchOrder(id) {
    const o = findOrder(id)
    return reply(o ? toDetail(o) : null, 250)
  },

  async refundOrder(id, req) {
    const o = findOrder(id)
    if (!o) throw notFound()
    // Idempotent op requestId: een herhaling (antwoord kwijt, dubbele klik) betaalt nooit twee keer.
    const seen = o.refunds?.find((r) => r.requestId === req.requestId)
    if (seen) {
      if (seen.amountCents !== req.amountCents) throw new ApiError('Deze terugbetaling is al verstuurd met een ander bedrag.', 409, undefined, 'idempotency_conflict')
      return reply(toDetail(o), 300)
    }
    const problem = refundProblem(stateOf(o), req.amountCents, req.reason)
    if (problem) fail(problem)
    if (!validRefundLines(o, req.lines)) throw new ApiError('Kies alleen regels en aantallen uit deze bestelling.', 422, { lines: 'Ongeldige regels' })

    const at = nowStamp()
    const reason = req.reason.trim()

    // Cadeaubon: naar rato van wat er via Mollie terugging; bij "alles" het hele bondeel.
    const voucherCents = o.voucherCents ?? 0
    const viaMollie = totalCentsOf(o) - voucherCents
    const full = req.amountCents >= refundableCents(stateOf(o))
    const voucherBack = voucherCents > 0 ? (full || viaMollie <= 0 ? voucherCents : Math.floor((voucherCents * req.amountCents) / viaMollie)) : 0
    const voucherRestoredCents = voucherBack > 0 ? restoreVoucher(o.id, `Terugbetaald: ${reason}`, voucherBack) : 0

    // Voorraad: alleen als Sarah aangeeft dat het ongeopend terug is. Zonder regels: alles wat nog niet terug was.
    const restockLines = req.restock ? (req.lines?.length ? req.lines : notYetRefunded(o)) : []
    if (restockLines.length) bookSaleStock(restockLines, 'webshop', 1, o.id, `Retour: ${reason}`)

    const updated = db.changeOrder(id, (row) => ({
      ...row,
      refunds: [
        ...(row.refunds ?? []),
        {
          id: `${demoId('R')}-${(row.refunds ?? []).length + 1}`,
          requestId: req.requestId,
          at,
          amountCents: req.amountCents,
          reason,
          ...(req.lines?.length ? { lines: req.lines } : {}),
          ...(restockLines.length ? { restocked: true } : {}),
          ...(voucherRestoredCents ? { voucherRestoredCents } : {}),
        },
      ],
      events: [...(row.events ?? []), orderEvent('terugbetaald', at, reason)],
    }))
    if (!updated) throw notFound()
    // Met Mollie duurt dit even; de demo doet alsof.
    return reply(toDetail(updated), 900)
  },

  async cancelOrder(id, req) {
    const o = findOrder(id)
    if (!o) throw notFound()
    if (o.cancelledAt && o.cancelRequestId === req.requestId) return reply(toDetail(o), 300)
    const problem = cancelProblem(stateOf(o))
    if (problem) fail(problem)
    const reason = req.reason.trim()
    if (!reason) throw new ApiError('Vul een reden in.', 422, { reason: 'Vul een reden in.' })

    const at = nowStamp()
    const paid = o.payment.status === 'betaald'
    // Via Mollie alleen wat er betaald is; het deel van de cadeaubon komt hieronder terug op de bon.
    const amountCents = refundableCents(stateOf(o))
    // Deels met een cadeaubon: dat deel komt terug op de bon, als eigen regel (zoals de server).
    const voucherBack = paid ? (o.voucherCents ?? 0) : 0
    const allLines = o.lines.map((l) => ({ productId: l.productId, qty: l.qty }))
    const cancelRefunds = [
      ...(paid && amountCents > 0 ? [{ id: `${demoId('R')}-cancel`, requestId: `${req.requestId}:refund`, at, amountCents, reason, lines: allLines }] : []),
      ...(voucherBack > 0
        ? [{ id: `${demoId('R')}-cancel-bon`, requestId: `${req.requestId}:voucher`, at, amountCents: voucherBack, reason: `${VOUCHER_REFUND_PREFIX}: ${reason}`, voucherRestoredCents: voucherBack }]
        : []),
    ]
    const updated = db.changeOrder(id, (row) => ({
      ...row,
      cancelledAt: at,
      cancelRequestId: req.requestId,
      // Betaald en nog niet ingepakt: annuleren is ook alles terugbetalen, inclusief verzending.
      refunds: cancelRefunds.length ? [...(row.refunds ?? []), ...cancelRefunds] : row.refunds,
      events: [
        ...(row.events ?? []),
        ...(cancelRefunds.length ? [orderEvent('terugbetaald', at, reason)] : []),
        orderEvent('geannuleerd', at, reason),
      ],
    }))
    if (!updated) throw notFound()
    adjustStock(o.lines, 1, o.id)
    if (o.voucherCents) restoreVoucher(o.id, `Bestelling geannuleerd: ${reason}`)
    return reply(toDetail(updated), 800)
  },

  async fetchSalesReport(from, to, groupBy, channel) {
    if (!isIsoDate(from) || !isIsoDate(to) || from > to) throw new ApiError('Kies een geldige periode.', 422, { from: 'Ongeldige periode' })
    return reply({ ...salesReport(allRevenue(), from, to, groupBy, channel), stock: stockPeriod(db.stockMovements(), from, to) }, 400)
  },

  async exportSales(from, to) {
    if (!isIsoDate(from) || !isIsoDate(to) || from > to) throw new ApiError('Kies een geldige periode.', 422)
    return reply(salesCsv(allRevenue(), from, to), 500)
  },
}
